<template>
  <admin-input-list-wrapper>
    <template #title>
      <span class="font-weight-medium">
        {{ $i18n.t('admin.usersGroups') }}
      </span>
    </template>
    <template #button>
      <base-button
        @click="addGroupHandler"
        translation-path="admin.addUsersGroup"
        style="margin-right: 0px !important"
      ></base-button>
    </template>
    <template #search>
      <data-input
        :data-type="{ name: 'text' }"
        prepend-inner-icon="mdi-magnify"
        :placeholder="$i18n.t('default.find')"
        v-model="search"
        clearable
        height="32"
        hide-details
        class="searchInput"
      >
      </data-input>
    </template>
    <template #list>
      <v-list dense>
        <template v-for="(value, key) in searchedItems">
          <users-groups-input-list-item-wrapper
            :key="key"
            v-bind="value"
            @dotsItemClicked="onDotsItemClicked"
          ></users-groups-input-list-item-wrapper>
        </template>
      </v-list>
    </template>
    <template #dialogs>
      <main-dialog
        v-if="isDialogLoaded"
        :is-visible.sync="isDialogVisible"
        :title="$i18n.t('dialog.title.deleteGroup')"
        :body="$i18n.t('dialog.body.deleteGroup', { name: payload.name })"
        agree-text="menu.delete"
        @agree="deleteGroupHandler"
        :loading="isConfirmButtonLoading"
      >
      </main-dialog>
      <main-input-dialog
        dialog-width="40%"
        v-if="isInputDialogLoaded"
        :is-visible.sync="isInputDialogVisible"
        :title-translation="$i18n.t(inputDialogTitle)"
        :agree-text="inputDialogAgreeButton"
        @agree="inputDialogAction"
        :loading="isAgreeButtonLoading"
        :inputs="inputs"
        :reset-on-close="false"
      >
        <template #upperForm v-if="dialogType === 'copy'">
          <italic-subheader translation-path="dialog.body.copyGroup"></italic-subheader>
        </template>
        <template #name.input="slot">
          <v-col>
            <v-row no-gutters>
              <v-col cols="12">
                <data-input v-model="payload.name" v-bind="slot" v-on="$listeners" :rules="slot.rules"></data-input>
              </v-col>
            </v-row>
          </v-col>
        </template>
        <template #color.input="{dataType}">
          <v-col class="pt-2">
            <v-row no-gutters>
              <v-col cols="auto">
                <data-input v-model="payload.color" :data-type="dataType"> </data-input>
              </v-col>
            </v-row>
          </v-col>
        </template>
      </main-input-dialog>
      <users-groups-permissions-dialog
        v-if="isPermissionsDialogLoaded"
        :is-visible.sync="isPermissionsDialogVisible"
        :group-id="id"
      ></users-groups-permissions-dialog>
    </template>
  </admin-input-list-wrapper>
</template>

<script>
import { get, call } from 'vuex-pathify';
import DataInput from '@/components/DataInput';
import ItalicSubheader from '@/components/ItalicSubheader';
import UsersGroupsInputListItemWrapper from '@/components/UsersGroupsInputListItemWrapper';
import AdminInputListWrapper from '@/components/AdminInputListWrapper';

export default {
  name: 'UsersInput',
  components: {
    DataInput,
    ItalicSubheader,
    UsersGroupsInputListItemWrapper,
    AdminInputListWrapper,
    MainDialog: () => import('@/components/MainDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
    UsersGroupsPermissionsDialog: () => import('@/components/UsersGroupsPermissionsDialog'),
  },
  data: () => ({
    search: '',
    id: undefined,
    payload: {
      name: undefined,
      color: '#000000',
    },
    isDialogVisible: false,
    isDialogLoaded: false,
    dialogType: undefined,
    isAgreeButtonLoading: false,
    isInputDialogVisible: false,
    isInputDialogLoaded: false,
    isConfirmButtonLoading: false,
    isPermissionsDialogLoaded: false,
    isPermissionsDialogVisible: false,
  }),
  computed: {
    userGroupNameMaxLength: get('admin/settingsValues@user_group_name_max_length'),
    groups: get('users/groups'),
    searchedItems() {
      if (!this.search) {
        return this.sortItems();
      }
      return Object.values(this.sortItems()).filter(group =>
        group.name.toLowerCase().includes(this.search.toLowerCase())
      );
    },
    inputDialogTitle() {
      return `dialog.title.${this.dialogType}Group`;
    },
    inputDialogBody() {
      return `dialog.body.${this.dialogType}Group`;
    },
    isEdit() {
      return this.dialogType === 'edit';
    },
    inputDialogAgreeButton() {
      return this.isEdit ? `dialog.agreeSave` : 'dialog.agreeAdd';
    },
    inputDialogAction() {
      return this[`${this.dialogType}Group`];
    },
    action() {
      return this[`${this.dialogType}GroupAction`];
    },
    inputs() {
      return [
        {
          name: 'name',
          translationPath: 'dialog.body.name',
          hint: 'dialog.body.createGroupName',
          rules: this.isEdit ? [] : [v => !!v || 'rules.required'],
          dataType: {
            name: 'text',
            max_length: this.userGroupNameMaxLength,
          },
        },
        {
          align: 'center',
          name: 'color',
          'validate-on-blur': true,
          translationPath: 'dialog.body.groupColor',
          rules: [v => !!v || 'rules.required'],
          dataType: {
            name: 'color',
          },
        },
      ];
    },
    currentGroup() {
      return this.groups[this.id];
    },
  },
  watch: {
    isInputDialogVisible(nV) {
      if (!nV) {
        this.resetPayload();
      }
    },
    isDialogVisible(nV) {
      if (!nV) {
        this.resetPayload();
      }
    },
  },
  methods: {
    addGroupAction: call('users/addGroup'),
    editGroupAction: call('users/editGroup'),
    copyGroupAction: call('users/copyGroup'),
    deleteGroupAction: call('users/deleteGroup'),
    onDotsItemClicked(data) {
      const { id, type } = data;
      this.id = id;
      const { name, color } = this.currentGroup;
      this.payload = { name, color };
      if (type === 'delete') {
        this.toggleConfirmationDialog(true);
      } else if (type === 'managePermissions') {
        this.openPermissionsDialog();
      } else {
        this.openAddGroupModal(type);
      }
    },
    addGroupHandler() {
      this.payload.color = this.$_getRandomColor();
      this.openAddGroupModal('add');
    },
    openPermissionsDialog() {
      this.isPermissionsDialogLoaded = true;
      this.isPermissionsDialogVisible = true;
    },
    resetPayload() {
      this.payload.color = '#000000';
      this.payload.name = undefined;
      this.id = undefined;
    },
    toggleConfirmationDialog(isVisible) {
      this.isDialogLoaded = isVisible;
      this.isDialogVisible = isVisible;
      if (!isVisible) {
        this.isConfirmButtonLoading = false;
      }
    },
    async deleteGroupHandler() {
      try {
        this.isConfirmButtonLoading = true;
        await this.deleteGroupAction(this.id);
      } catch (error) {
        console.log('handleBlockUser -> error', error);
      }
      this.toggleConfirmationDialog(false);
    },
    openAddGroupModal(type) {
      this.dialogType = type;
      this.isInputDialogVisible = true;
      this.isInputDialogLoaded = true;
    },
    addGroup() {
      this.actionsWrapper(this.payload);
    },
    editGroup() {
      const payload = {
        id: this.id,
        body: this.payload,
      };
      this.actionsWrapper(payload);
    },
    copyGroup() {
      this.actionsWrapper({
        id: this.id,
        body: this.payload,
      });
    },
    async actionsWrapper(payload) {
      try {
        this.isAgreeButtonLoading = true;
        await this.action(payload);
      } catch (error) {
        console.log('editAddWrapper -> error', error);
      }
      this.isAgreeButtonLoading = false;
      this.isInputDialogVisible = false;
    },
    sortItems() {
      return this.$_objectToArray(this.groups)?.sort((a, b) => b.id - a.id);
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .searchInput {
    .v-input__slot {
      min-height: 0px !important;
      padding: 0 4px !important;
    }
    .v-input__append-inner {
      margin: 6px !important;
    }
    .v-input__prepend-inner {
      .v-input__icon {
        .v-icon {
          font-size: 16px;
          padding-bottom: 5px;
        }
      }
    }
    .v-text-field__slot input {
      font-size: 12px !important;
    }
  }
}
</style>
